import { useEffect, useState } from "react";
import {
  createAlienLaserBurst,
  randomAlienLaserHold,
  randomAlienLaserPause,
} from "./alien-lasers";

// Per-skin decoration painted behind the popup (z-index 0, under `.popup`).
// Everything here is inert: aria-hidden, pointer-events none, and animated
// with transform/opacity keyframes only, following the same declarative
// SVG/DOM approach as the ambiance tile atmospheres.

const MATRIX_GLYPHS = "ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ0123456789";

// Deterministic per-column glyph runs: a real random() would reshuffle the
// whole backdrop on every React re-render.
function glyphColumn(seed, length) {
  let state = seed * 1103515245 + 12345;
  let out = "";
  for (let i = 0; i < length; i += 1) {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    out += MATRIX_GLYPHS[state % MATRIX_GLYPHS.length];
  }
  return out;
}

const MATRIX_COLUMNS = [
  { left: 1, dur: 6.2, delay: 0, len: 22, opacity: 0.5, size: 13 },
  { left: 7, dur: 9.4, delay: 1.8, len: 18, opacity: 0.28, size: 11 },
  { left: 13, dur: 7.1, delay: 0.6, len: 26, opacity: 0.42, size: 14 },
  { left: 19, dur: 11.2, delay: 2.9, len: 16, opacity: 0.22, size: 10 },
  { left: 25, dur: 5.6, delay: 1.2, len: 24, opacity: 0.55, size: 13 },
  { left: 31, dur: 8.8, delay: 0.2, len: 20, opacity: 0.3, size: 12 },
  { left: 37, dur: 6.8, delay: 3.4, len: 28, opacity: 0.45, size: 14 },
  { left: 43, dur: 10.4, delay: 1.5, len: 17, opacity: 0.24, size: 11 },
  { left: 49, dur: 7.6, delay: 0.9, len: 23, opacity: 0.38, size: 13 },
  { left: 55, dur: 5.9, delay: 2.4, len: 25, opacity: 0.52, size: 12 },
  { left: 61, dur: 9.9, delay: 0.4, len: 19, opacity: 0.26, size: 10 },
  { left: 67, dur: 7.3, delay: 1.9, len: 27, opacity: 0.4, size: 14 },
  { left: 73, dur: 6.4, delay: 3.1, len: 21, opacity: 0.48, size: 13 },
  { left: 79, dur: 10.8, delay: 0.7, len: 18, opacity: 0.22, size: 11 },
  { left: 85, dur: 8.2, delay: 2.2, len: 24, opacity: 0.34, size: 12 },
  { left: 91, dur: 6.6, delay: 1.4, len: 26, opacity: 0.46, size: 13 },
  { left: 96, dur: 9.1, delay: 0.1, len: 20, opacity: 0.28, size: 11 },
];

function MatrixBackdrop() {
  return (
    <div className="skin-backdrop__matrix">
      {MATRIX_COLUMNS.map((column, index) => (
        <span
          key={index}
          className="skin-backdrop__matrix-column"
          style={{
            left: `${column.left}%`,
            fontSize: `${column.size}px`,
            opacity: column.opacity,
            animationDelay: `-${column.delay}s`,
            animationDuration: `${column.dur}s`,
          }}
        >
          {glyphColumn(index + 1, column.len)
            .split("")
            .map((glyph, glyphIndex, all) => (
              <span
                key={glyphIndex}
                className={
                  glyphIndex === all.length - 1
                    ? "skin-backdrop__matrix-glyph skin-backdrop__matrix-glyph--head"
                    : "skin-backdrop__matrix-glyph"
                }
              >
                {glyph}
              </span>
            ))}
        </span>
      ))}
    </div>
  );
}

const BUBBLES = [
  { left: 5, size: 7, dur: 11.5, delay: 0, drift: 6, opacity: 0.34 },
  { left: 12, size: 4, dur: 8.4, delay: 2.6, drift: -5, opacity: 0.26 },
  { left: 19, size: 10, dur: 14.2, delay: 1.1, drift: 8, opacity: 0.3 },
  { left: 26, size: 5, dur: 9.6, delay: 4.2, drift: -7, opacity: 0.4 },
  { left: 34, size: 3, dur: 7.8, delay: 0.5, drift: 4, opacity: 0.22 },
  { left: 41, size: 8, dur: 12.8, delay: 3.3, drift: -6, opacity: 0.32 },
  { left: 48, size: 5, dur: 10.2, delay: 1.8, drift: 7, opacity: 0.36 },
  { left: 56, size: 12, dur: 15.6, delay: 5.1, drift: -9, opacity: 0.24 },
  { left: 63, size: 4, dur: 8.9, delay: 2.2, drift: 5, opacity: 0.38 },
  { left: 70, size: 9, dur: 13.4, delay: 0.9, drift: -8, opacity: 0.28 },
  { left: 77, size: 6, dur: 10.8, delay: 3.9, drift: 6, opacity: 0.34 },
  { left: 84, size: 3, dur: 7.4, delay: 1.4, drift: -4, opacity: 0.2 },
  { left: 90, size: 8, dur: 12.1, delay: 4.7, drift: 7, opacity: 0.3 },
  { left: 96, size: 5, dur: 9.2, delay: 2.9, drift: -5, opacity: 0.26 },
];

function AquariumBackdrop() {
  return (
    <div className="skin-backdrop__aquarium">
      {BUBBLES.map((bubble, index) => (
        <span
          key={index}
          className="skin-backdrop__bubble"
          style={{
            left: `${bubble.left}%`,
            width: `${bubble.size}px`,
            height: `${bubble.size}px`,
            opacity: bubble.opacity,
            "--bubble-drift": `${bubble.drift}px`,
            animationDelay: `-${bubble.delay}s`,
            animationDuration: `${bubble.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

// Mac OS 8 Platinum window chrome from classic-ui-kit: desktop behind, a
// bevelled window, and a striped titlebar with the close/collapse/zoom boxes.
function MacOsBackdrop() {
  return (
    <div className="skin-backdrop__macos">
      <div className="skin-backdrop__macos-window">
        <div className="skin-backdrop__macos-titlebar">
          <span className="skin-backdrop__macos-box skin-backdrop__macos-box--close" />
          <span className="skin-backdrop__macos-stripes" />
          <span className="skin-backdrop__macos-title">iSlowed</span>
          <span className="skin-backdrop__macos-stripes" />
          <span className="skin-backdrop__macos-box skin-backdrop__macos-box--collapse" />
          <span className="skin-backdrop__macos-box skin-backdrop__macos-box--zoom" />
        </div>
      </div>
    </div>
  );
}

// Winamp 2 main window: bevelled grey shell with the small striped titlebar
// and its shade/close buttons.
function WinampBackdrop() {
  return (
    <div className="skin-backdrop__winamp">
      <div className="skin-backdrop__winamp-window">
        <div className="skin-backdrop__winamp-titlebar">
          <span className="skin-backdrop__winamp-menu" />
          <span className="skin-backdrop__winamp-stripes" />
          <span className="skin-backdrop__winamp-title">SLOWED</span>
          <span className="skin-backdrop__winamp-stripes" />
          <span className="skin-backdrop__winamp-button skin-backdrop__winamp-button--min" />
          <span className="skin-backdrop__winamp-button skin-backdrop__winamp-button--shade" />
          <span className="skin-backdrop__winamp-button skin-backdrop__winamp-button--close" />
        </div>
      </div>
    </div>
  );
}

// Oversized storm clouds roll behind the Dracula popup.
const DRACULA_CLOUDS = [
  { top: 3, width: 440, height: 170, dur: 39, delay: 0, drift: 26, opacity: 0.5 },
  { top: 34, width: 360, height: 145, dur: 46, delay: 17, drift: -18, opacity: 0.36 },
  { top: 64, width: 480, height: 185, dur: 52, delay: 31, drift: 14, opacity: 0.3 },
];

function DraculaBackdrop() {
  return (
    <div className="skin-backdrop__dracula">
      {DRACULA_CLOUDS.map((cloud, index) => (
        <span
          key={`cloud-${index}`}
          className="skin-backdrop__dracula-cloud"
          style={{
            top: `${cloud.top}%`,
            width: `${cloud.width}px`,
            height: `${cloud.height}px`,
            opacity: cloud.opacity,
            "--cloud-drift": `${cloud.drift}px`,
            animationDelay: `-${cloud.delay}s`,
            animationDuration: `${cloud.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

function AlienBackdrop() {
  const [burst, setBurst] = useState(() => createAlienLaserBurst());

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setBurst([]);
      return undefined;
    }

    let showTimer;
    let hideTimer;
    let disposed = false;

    const scheduleBurst = () => {
      showTimer = setTimeout(() => {
        if (disposed) return;
        setBurst(createAlienLaserBurst());
        hideTimer = setTimeout(() => {
          if (disposed) return;
          setBurst([]);
          scheduleBurst();
        }, randomAlienLaserHold());
      }, randomAlienLaserPause());
    };

    hideTimer = setTimeout(() => {
      setBurst([]);
      scheduleBurst();
    }, 700);

    return () => {
      disposed = true;
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div className="skin-backdrop__alien-lasers">
      {burst.map((laser, index) => (
        <span
          key={`${index}-${laser.left}-${laser.top}`}
          className="skin-backdrop__alien-laser"
          style={{
            left: `${laser.left}%`,
            top: `${laser.top}%`,
            width: `${laser.width}px`,
            "--laser-angle": `${laser.angle}deg`,
            animationDelay: `${laser.delay}ms`,
            animationDuration: `${laser.duration}ms`,
          }}
        />
      ))}
    </div>
  );
}

const BACKDROPS = {
  matrix: MatrixBackdrop,
  aquarium: AquariumBackdrop,
  macos: MacOsBackdrop,
  winamp: WinampBackdrop,
  dracula: DraculaBackdrop,
  alien: AlienBackdrop,
};

export default function SkinBackdrop({ skin }) {
  const Backdrop = BACKDROPS[skin];
  if (!Backdrop) return null;

  return (
    <div className={`skin-backdrop skin-backdrop--${skin}`} aria-hidden="true">
      <Backdrop />
    </div>
  );
}
